import { useQuery, skipToken } from "@tanstack/react-query";
import {
  Article,
  Artist,
  Bundle,
  Comic,
  ContentCardProps,
  Release,
} from "../../types";
import { ComicCard } from "./ComicCard";
import { ArtistCard } from "./ArtistCard";
import { NewsCard } from "./NewsCard";
import { ReleaseCard } from "./ReleaseCard";
import { BundleCard } from "./BundleCard";
import { useToggleComicFollow } from "./hooks/useToggleComicFollow";
import { useToggleArtistFollow } from "./hooks/useToggleArtistFollow";
import { memo } from "react";

export const ContentCard = memo(
  ({
    content,
    layout,
    section,
    fullWidth,
    dontOpenInfo = false,
    index,
    sectionIndex,
  }: ContentCardProps) => {
    const context = layout.style.context;
    const cacheKey = `${content?.entity_type}-${content?.id}`;

    const { data } = useQuery<Comic | Artist | Article | Release | Bundle>({
      queryKey: [cacheKey],
      queryFn: skipToken,
      networkMode: "offlineFirst",
      refetchOnWindowFocus: false,
    });
    // Prefer data cached in query client
    const dataToUse = data ?? content;

    /**
     * image_small_url
     * image_medium_url
     * image_tiny_url
     * image_url
     */

    const handleToggleComicFollow =
      useToggleComicFollow(cacheKey).handleToggleFollow;
    const { handleToggleArtistFollow } = useToggleArtistFollow(cacheKey);

    switch (context) {
      case "comics": {
        const typedContent = dataToUse as Comic;
        typedContent.image_url = getImageUrl(typedContent);
        return (
          <ComicCard
            onToggleFollow={handleToggleComicFollow}
            section={section}
            content={typedContent}
            layout={layout}
            fullWidth={fullWidth}
            dontOpenInfo={dontOpenInfo}
            index={index}
            sectionIndex={sectionIndex}
          />
        );
      }
      case "creators": {
        const typedContent = dataToUse as Artist;
        return (
          <ArtistCard
            section={section}
            onToggleFollow={handleToggleArtistFollow}
            content={typedContent}
            index={index}
            sectionIndex={sectionIndex}
            fullWidth={fullWidth}
            layout={{
              ...layout,
              display: {
                ...layout.display,
                include_flag: layout.style.size !== "s" ? true : false,
              },
            }}
            dontOpenInfo={dontOpenInfo}
          />
        );
      }
      case "news": {
        const typedContent = dataToUse as Article;
        return (
          <NewsCard
            section={section}
            content={typedContent}
            index={index}
            sectionIndex={sectionIndex}
            layout={layout}
            fullWidth={fullWidth}
          />
        );
      }
      case "releases": {
        const typedContent = dataToUse as Release;
        typedContent.image_url = getImageUrl(typedContent);
        return (
          <ReleaseCard
            section={section}
            content={typedContent}
            index={index}
            sectionIndex={sectionIndex}
            layout={layout}
            fullWidth={fullWidth}
          />
        );
      }
      case "bundle": {
        const typedContent = dataToUse as Bundle;
        return (
          <BundleCard
            content={typedContent}
            layout={layout}
            section={section}
            index={index}
            sectionIndex={sectionIndex}
            expanded={fullWidth}
          />
        );
      }
    }

    console.warn(
      `Unable to determine type of ContentCard by context: ${context}`,
    );
    return null;
  },
  (prevProps, nextProps) => {
    // Only re-render if primitive values or IDs change
    return (
      prevProps.content.id === nextProps.content.id &&
      prevProps.layout.style.context === nextProps.layout.style.context &&
      prevProps.layout.style.mode === nextProps.layout.style.mode &&
      prevProps.section?.source_type === nextProps.section?.source_type &&
      prevProps.fullWidth === nextProps.fullWidth &&
      prevProps.dontOpenInfo === nextProps.dontOpenInfo &&
      prevProps.index === nextProps.index &&
      prevProps.sectionIndex === nextProps.sectionIndex
    );
  },
);

ContentCard.displayName = "ContentCard";

function getImageUrl(data: Comic | Release) {
  if (data.image_small_url) {
    return data.image_small_url;
  }

  return data.image_url;
}
